import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _isFinite from 'lodash/isFinite';

import { eventBus } from '@atc/cai-event-bus';
import { formatCurrency } from '@atc/string-fns';
import { calculatePersonalPayment } from '@atc/wallet-payment-fns';

import { LoadingSpinner, SelectableTabPanel, SelectableTabs, Text } from 'reaxl';

import { defaultPaymentInfo } from 'axl-config';

import usePaymentData from '@/hooks/usePaymentData';

const tabLabels = {
    finance: 'Finance',
    lease: 'Lease',
    cash: 'Cash',
};

function DealTypeTabs({
    supportedDealTypes = ['finance', 'cash'],
    initialDealType = 'finance',
    includeTaxesFees = false,
}) {
    const [activeTab, setActiveTab] = useState();
    const [isLoading, hasError, paymentData] = usePaymentData(supportedDealTypes);
    const [financeOffersObj, setFinanceOffersObj] = useState({});
    const [leaseOffersObj, setLeaseOffersObj] = useState({});
    const [cashOffersObj, setCashOffersObj] = useState({});

    useEffect(() => {
        const handleCalcPencilingDataUpdateRequest = (data) => {
            const dealType = data.dealType?.toLowerCase();
            if (dealType && dealType !== activeTab) {
                // calls only when dealType get updated via sdp
                setActiveTab(dealType);
            }
        };

        if (!activeTab) {
            // calls on page load or VDP TO VDP transition
            setActiveTab(initialDealType);
        }

        eventBus.on(
            'uds:calcPencilingDataUpdate:updated',
            handleCalcPencilingDataUpdateRequest
        );

        return () => {
            eventBus.off(
                'uds:calcPencilingDataUpdate:updated',
                handleCalcPencilingDataUpdateRequest
            );
        };
    }, [activeTab, initialDealType]);

    useEffect(() => {
        if (paymentData) {
            const { finance, lease, cash } = tabLabels;

            paymentData?.map(
                (item) => {
                    switch (item?.paymentLabel?.toLowerCase()) {
                        case finance?.toLowerCase():
                            setFinanceOffersObj(item);
                            break;
                        case lease?.toLowerCase():
                            setLeaseOffersObj(item);
                            break;
                        case cash?.toLowerCase():
                            setCashOffersObj(item);
                            break;
                        default:
                            break;
                    }
                }
            );
        }
    }, [paymentData]);

    const onTabSelect = (key = 'finance', event) => {
        // Remove the '-payment-summary' suffix from the key/id to ensure the correct deal type is set
        key = key.split('-')[0];
        eventBus?.emit('uds:setDealType:request', {
            dealType: key,
            placement: 'deal-type-tabs',
            event,
        });
    };

    const formatPayment = (errorState, payment) => ({
        value: formatCurrency(errorState || !_isFinite(payment) ? '---' : Math.floor(payment)),
        isPaymentAmountInvalid: !errorState && !_isFinite(payment) && !payment,
    });

    const getFinancePayment = () => {
        const { payment, pricingDetails, customRate } = financeOffersObj;
        if (!customRate) {
            return payment;
        }

        return Math.max(0, calculatePersonalPayment(pricingDetails?.salePrice, financeOffersObj || defaultPaymentInfo, customRate)) || 0;
    };

    const renderTabHeaderContent = (tabName) => {
        const titles = { finance: 'Finance', lease: 'Lease', cash: 'Cash' };
        const dealValues = {
            finance: formatPayment(hasError || financeOffersObj.isPaymentError, getFinancePayment()),
            lease: formatPayment(hasError || leaseOffersObj.isPaymentError, leaseOffersObj.payment),
            cash: formatPayment(hasError, Math.max(0, cashOffersObj.payment)),
        };

        return (
            <>
                <Text color="subdued-lighter">{titles[tabName]}</Text>
                <div className="margin-top-1">
                    {isLoading || dealValues[tabName].isPaymentAmountInvalid ? (
                        <LoadingSpinner
                            height={24}
                            className="display-flex align-items-center margin-0 margin-horizontal-auto"
                        />
                    ) : (
                        <>
                            <Text
                                size={400}
                                weight="bold"
                            >
                                {dealValues[tabName].value}
                            </Text>
                            {tabName !== 'cash' && (
                                <Text size={200}>/mo.</Text>
                            )}
                            {includeTaxesFees && (
                                <Text
                                    size={200}
                                    className={clsx({ 'text-valign-super': tabName === 'cash' })}
                                >
                                    *
                                </Text>
                            )}
                        </>
                    )}
                </div>
            </>
        );
    };

    const renderIncentiveIcon = (tabName) => {
        const tabIncentiveMap = {
            finance: financeOffersObj?.hasOffersOrIncentives,
            cash: cashOffersObj?.hasOffersOrIncentives,
            lease: leaseOffersObj?.hasOffersOrIncentives,
        };
        const incentiveIcon = (
            <svg
                width="11"
                height="11"
                viewBox="0 0 11 11"
            >
                <path
                    d="M10.8437 6.2457L4.98877 0.370508C4.90203 0.283789 4.72855 0.21875 4.62012 0.21875H1.25897L0.5 0.869141V4.33789C0.5 4.46797 0.565055 4.61973 0.651794 4.70645L6.50671 10.56C6.59345 10.6467 6.72356 10.6467 6.81029 10.56L10.822 6.5709C10.9304 6.48418 10.9304 6.33242 10.8437 6.2457ZM2.01794 2.99375C1.67098 2.64687 1.67098 2.10488 2.01794 1.75801C2.3649 1.41113 2.90702 1.41113 3.25398 1.75801C3.60094 2.10488 3.60094 2.64687 3.25398 2.99375C2.9287 3.31895 2.3649 3.31895 2.01794 2.99375Z"
                    fill="white"
                />
            </svg>
        );

        return tabIncentiveMap[tabName] ? incentiveIcon : null;
    };

    const activeTabKey = activeTab && supportedDealTypes ? supportedDealTypes?.indexOf(activeTab) : 0;

    const selectableTabsContent = supportedDealTypes.map(
        (tabName, index) => tabLabels[tabName] && (
            <SelectableTabPanel
                key={tabName}
                icon={renderIncentiveIcon(tabName)}
                tabHeaderContent={renderTabHeaderContent(tabName)}
                inputProps={{
                    id: tabName + '-payment-summary',
                    name: 'estimatedPaymentTabs',
                    // Should be Removed once we address - US1553680: UDS SelectedTab Dealtype Radio button Not Updating the Checked attribute on the DOM
                    ...(activeTabKey === index && { 'data-cmp': 'activeTab' }),
                }}
            />
        )
    );

    return (
        <>
            <SelectableTabs
                grouped
                defaultActiveKey={activeTabKey}
                // during cleanup of FF change the params of onTabSelect
                // from (key, event) to (event, key) and get rid of below anon fn
                onSelect={(event, key) => {
                    onTabSelect(key, event);
                }}
            >
                {selectableTabsContent}
            </SelectableTabs>
            {(!isLoading && !hasError && includeTaxesFees) && (
                <Text
                    color="gray-dark"
                    componentClass="div"
                    className="margin-bottom-3"
                    size={200}
                >
                    * Includes est. taxes and fees
                </Text>
            )}
        </>
    );
}

DealTypeTabs.propTypes = {
    /**
    * Array containg strings of all the supported deal types
    */
    supportedDealTypes: PropTypes.array,
    /**
    * String that represents the initial deal type of the listing
    */
    initialDealType: PropTypes.string,
    includeTaxesFees: PropTypes.bool,
};

export default DealTypeTabs;
